import { create } from 'zustand';
import type { CustomerTimelineItemDto, FollowUpDto, FollowUpInput, FollowUpPatch } from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useWorkspaceStore } from './workspace.store';

/**
 * Follow-ups and the customer timeline (ADR-104).
 *
 * The list is the Follow-ups page — mine, everyone's, done — searched and paged
 * on the server. A customer's open ones and their timeline are held beside it
 * for the profile, and every change lands in whichever of them shows the row,
 * then asks the bell to count again.
 */

export type FollowUpView = 'mine' | 'everyone' | 'done';

const PAGE_SIZE = 20;

interface FollowUpsState {
  view: FollowUpView;
  search: string;
  items: FollowUpDto[];
  nextCursor: string | null;
  total: number | null;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;

  /** The profile's: one customer's open follow-ups, and their timeline. */
  customerId: string | null;
  customerItems: FollowUpDto[];
  timeline: CustomerTimelineItemDto[];
  timelineCursor: string | null;
  timelineLoading: boolean;

  saving: boolean;
  saveError: string | null;

  load: (patch?: { view?: FollowUpView; search?: string }) => Promise<void>;
  loadMore: () => Promise<void>;
  openCustomer: (customerId: string) => Promise<void>;
  loadMoreTimeline: () => Promise<void>;
  /** Created or changed; null when it could not be saved (the reason is in `saveError`). */
  save: (id: string | null, input: FollowUpInput | FollowUpPatch) => Promise<FollowUpDto | null>;
  done: (id: string) => Promise<void>;
  reopen: (id: string) => Promise<void>;
  remove: (id: string) => Promise<boolean>;
  clearSaveError: () => void;
}

export const useFollowUpsStore = create<FollowUpsState>((set, get) => {
  /** A changed row put where it shows — or taken out of a list it no longer belongs in. */
  function place(row: FollowUpDto) {
    const { view, items, customerId, customerItems } = get();
    const belongs = view === 'done' ? row.doneAt !== null : row.doneAt === null;
    const inList = items.some((entry) => entry.id === row.id);
    set({
      items: inList ? (belongs ? items.map((entry) => (entry.id === row.id ? row : entry)) : items.filter((entry) => entry.id !== row.id)) : items,
      customerItems:
        row.customer?.id === customerId
          ? row.doneAt
            ? customerItems.filter((entry) => entry.id !== row.id)
            : [row, ...customerItems.filter((entry) => entry.id !== row.id)].sort((a, b) => a.dueAt.localeCompare(b.dueAt))
          : customerItems,
    });
    void useWorkspaceStore.getState().refreshAttention();
    // The timeline shows follow-ups too: read it again from the top.
    if (customerId && row.customer?.id === customerId) void get().openCustomer(customerId);
  }

  function query(view: FollowUpView, search: string) {
    return {
      limit: PAGE_SIZE,
      search: search || undefined,
      assignee: view === 'mine' ? 'me' : 'anyone',
      status: view === 'done' ? ('done' as const) : ('open' as const),
    };
  }

  return {
    view: 'mine',
    search: '',
    items: [],
    nextCursor: null,
    total: null,
    loading: false,
    loadingMore: false,
    error: null,
    customerId: null,
    customerItems: [],
    timeline: [],
    timelineCursor: null,
    timelineLoading: false,
    saving: false,
    saveError: null,

    async load(patch) {
      const view = patch?.view ?? get().view;
      const search = patch?.search ?? get().search;
      set({ view, search, loading: true, error: null });
      try {
        const page = await withSession(() => adminApi.crm.followUps(query(view, search)));
        set({ items: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, loading: false });
      } catch (error) {
        set({ loading: false, error: errorMessage(error, 'Could not load follow-ups.') });
      }
    },

    async loadMore() {
      const { nextCursor, loadingMore, view, search, items } = get();
      if (!nextCursor || loadingMore) return;
      set({ loadingMore: true });
      try {
        const page = await withSession(() => adminApi.crm.followUps({ ...query(view, search), cursor: nextCursor }));
        set({ items: [...items, ...page.items], nextCursor: page.meta.nextCursor, loadingMore: false });
      } catch (error) {
        set({ loadingMore: false, error: errorMessage(error, 'Could not load more follow-ups.') });
      }
    },

    async openCustomer(customerId) {
      // Cleared when it is another customer: one person's history must never show under another's name.
      if (get().customerId !== customerId) set({ customerId, customerItems: [], timeline: [], timelineCursor: null });
      set({ timelineLoading: true });
      const locale = useUiStore.getState().locale;
      const [open, feed] = await Promise.allSettled([
        withSession(() => adminApi.crm.followUps({ customerId, status: 'open', limit: 50 })),
        withSession(() => adminApi.crm.timeline(customerId, { limit: PAGE_SIZE, locale })),
      ]);
      if (get().customerId !== customerId) return;
      set({
        customerItems: open.status === 'fulfilled' ? open.value.items : [],
        timeline: feed.status === 'fulfilled' ? feed.value.items : [],
        timelineCursor: feed.status === 'fulfilled' ? feed.value.meta.nextCursor : null,
        timelineLoading: false,
      });
    },

    async loadMoreTimeline() {
      const { customerId, timelineCursor, timelineLoading, timeline } = get();
      if (!customerId || !timelineCursor || timelineLoading) return;
      set({ timelineLoading: true });
      try {
        const locale = useUiStore.getState().locale;
        const page = await withSession(() => adminApi.crm.timeline(customerId, { limit: PAGE_SIZE, cursor: timelineCursor, locale }));
        set({ timeline: [...timeline, ...page.items], timelineCursor: page.meta.nextCursor, timelineLoading: false });
      } catch {
        set({ timelineLoading: false });
      }
    },

    async save(id, input) {
      set({ saving: true, saveError: null });
      try {
        const row = await withSession(() =>
          id ? adminApi.crm.update(id, input as FollowUpPatch) : adminApi.crm.create(input as FollowUpInput),
        );
        set({ saving: false });
        if (!id) {
          // A new one joins the page's list when it would be in it.
          const { view, items } = get();
          if (view !== 'done') set({ items: [row, ...items].sort((a, b) => a.dueAt.localeCompare(b.dueAt)) });
        }
        place(row);
        return row;
      } catch (error) {
        set({ saving: false, saveError: errorMessage(error, 'Could not save the follow-up.') });
        return null;
      }
    },

    async done(id) {
      try {
        place(await withSession(() => adminApi.crm.done(id)));
      } catch (error) {
        set({ error: errorMessage(error, 'Could not mark it done.') });
      }
    },

    async reopen(id) {
      try {
        place(await withSession(() => adminApi.crm.reopen(id)));
      } catch (error) {
        set({ error: errorMessage(error, 'Could not reopen it.') });
      }
    },

    async remove(id) {
      set({ saving: true, saveError: null });
      try {
        await withSession(() => adminApi.crm.remove(id));
        const { customerId } = get();
        set((state) => ({
          saving: false,
          items: state.items.filter((entry) => entry.id !== id),
          customerItems: state.customerItems.filter((entry) => entry.id !== id),
        }));
        void useWorkspaceStore.getState().refreshAttention();
        if (customerId) void get().openCustomer(customerId);
        return true;
      } catch (error) {
        set({ saving: false, saveError: errorMessage(error, 'Could not delete the follow-up.') });
        return false;
      }
    },

    clearSaveError() {
      set({ saveError: null });
    },
  };
});
